/**
 * Générateur — Automatisme : repérer un nombre relatif sur une droite graduée
 * (lire l'abscisse d'un point).
 * Attendu ID : 1235 | Niveau : 3ème | Domaine : Espace et géométrie, Repérage
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 l. 1061-1066 ; mêmes mots que
 * l'automatisme 4e l. 982-987, sans gen en base : ce gen sert les deux).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * EXTENSION de gen_1065 (5e, demi-droite) à la droite graduée RELATIVE, mêmes
 * régimes que 1234 (droiteRelative.js : entiers, demis, dixièmes). La saisie
 * interroge un point NÉGATIF seulement (pour un positif, « 2 » et « +2 »
 * seraient tous deux justes, gen_706), réponse au tiret du clavier. Repères
 * non adjacents (944) exclus ; encadrer = « entre −3 et −2 » (À ARBITRER).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Quelle abscisse ? » : saisie (A négatif, avec son signe) ; QCM
 *     à leurres calculés [−2,7 ; −1,3 ; 2,7 ; −2,3].
 *   Carte 2 « Quel nombre ? » (nombre_saisie) : « Quel nombre est repéré par
 *     la 3e petite graduation à gauche de −2 ? » (−2,3) ; « juste à gauche
 *     de −3 » (−3,1).
 *   Carte 3 « Entre quels entiers ? » : QCM « A est entre… » [−3 et −2 ; −2 et
 *     −1 ; 2 et 3 ; −4 et −3] ; V/F sur une droite portant A, B, C.
 *
 * INVARIANTS : dixièmes entiers ; figures avec data-droite / data-points ;
 * leurres distincts, dans la fenêtre ; le point interrogé n'est jamais sur un
 * entier étiqueté.
 * SACRIFIÉ (fiches) : −2,7, partagé avec 1234, exclu de tout tirage
 * (droiteRelative.js), auto-testé.
 *
 * ── Erreurs (familles nommées ; aucun item DEPP) ─────────────────────────
 *   sens_inverse_negatif  : A en −2,7 lu −1,3 (de −2, 7 graduations comptées vers 0), PRINCIPALE ;
 *   signe_inverse         : 2,7 ;
 *   rang_depuis_la_gauche : −2,3 (la 7e graduation après −3 lue « 7 ») ;
 *   decale_un_trait       : −2,6 / −2,8 ;
 *   encadrement_decale    : « entre −2 et −1 » pour −2,7 ;
 *   signe_oublie          : « entre 2 et 3 ».
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import {
    frD, saisieD, tirerRegime, tirerPoints, svgDroite, INTRO,
    leurresAbscisse, troisLeurres, grille, SACRIFIE_D,
} from './droiteRelative';

const ATTENDU_ID = 1235;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_ABSCISSE = 'Quelle abscisse ?';
const L_NOMBRE = 'Quel nombre ?';
const L_ENCADRER = 'Entre quels entiers ?';

const ORD = (n) => (n === 1 ? '1re' : `${n}e`);
const EXPLICATIONS = {
    regle: 'À gauche de 0, je lis depuis l\'entier le plus proche de 0 : A est 7 petites graduations à gauche de −2, son abscisse est −2,7.',
    sens_inverse_negatif: 'A est à gauche de −2, donc plus loin de 0 que −2 : −2,7, pas −1,3.',
    signe_inverse: 'A est à gauche de 0 : son abscisse est négative.',
    rang_depuis_la_gauche: 'Je compte depuis −2 (l\'entier le plus proche de 0), pas depuis −3 : 7 petites graduations, −2,7.',
    decale_un_trait: 'Je recompte les petites graduations une à une depuis −2.',
    encadrement_decale: '−2,7 est plus loin de 0 que −2 : entre −3 et −2.',
    signe_oublie: 'A est à gauche de 0 : entre −3 et −2, pas entre 2 et 3.',
};

/** Un point A négatif (ou positif si demandé) sur un régime tiré. */
const tirerA = ({ nom = null, negatif = true } = {}) => {
    for (let essai = 0; essai < 50; essai++) {
        const reg = tirerRegime(nom);
        const cands = grille(reg, (v) => (negatif ? v < 0 : v > 0));
        if (cands.length) return { reg, x: randomChoice(cands) };
    }
    const reg = tirerRegime('dixiemes');
    return { reg, x: randomChoice(grille(reg, (v) => v < 0)) };
};

// ─── Carte 1a : quelle abscisse ? (saisie, point négatif) ────────────────

const genAbscisseSaisie = () => {
    const { reg, x } = tirerA();
    const svg = svgDroite(reg, [{ d: x, nom: 'A' }]);
    const texte = `<p>${INTRO(reg)}</p><p>Quelle est l'abscisse du point A ? Écris-la avec son signe (ex. : -3 ou -2,6).</p>`;
    const payload = makeSaisie(saisieD(x), texte, { ...opts(L_ABSCISSE), elements_additionnels: imgElement(svg, 'large') });
    payload.contenu = { ...payload.contenu, sousType: reg.nom, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { regime: reg.nom, start: reg.start, end: reg.end, x };
    return payload;
};

// ─── Carte 1b : quelle abscisse ? (QCM à leurres calculés) ───────────────

const genAbscisseQcm = () => {
    for (let essai = 0; essai < 100; essai++) {
        const { reg, x } = tirerA({ negatif: randInt(1, 4) !== 1 });
        const faux = troisLeurres(leurresAbscisse(x, reg));
        if (!faux) continue;
        const bon = frD(x);
        const choix = [bon, ...faux.map((f) => frD(f.v))];
        if (new Set(choix).size !== 4) continue;
        const svg = svgDroite(reg, [{ d: x, nom: 'A' }]);
        const payload = makeQcm(choix, bon, `<p>${INTRO(reg)}</p><p><b>Quelle est l'abscisse du point A ?</b></p>`, { ...opts(L_ABSCISSE), elements_additionnels: imgElement(svg, 'large') });
        payload.contenu = { ...payload.contenu, sousType: reg.nom, explications: EXPLICATIONS };
        payload._meta.params = { regime: reg.nom, start: reg.start, end: reg.end, x, distracteurs: faux.map(({ code, v }) => ({ code, v })) };
        return payload;
    }
    throw new Error('gen_1235 : QCM abscisse sans leurres');
};

// ─── Carte 2 : quel nombre ? (saisie, réponse négative) ──────────────────

const genNombreSaisie = () => {
    const reg = tirerRegime(randomChoice(['demis', 'dixiemes']));
    // un entier étiqueté négatif ou nul, avec de la place à sa gauche
    const departs = []; for (let v = reg.start + 10; v <= Math.min(0, reg.end); v += 10) departs.push(v);
    const depart = randomChoice(departs);
    const juste = reg.div === 2 || randInt(0, 1) === 1;
    let r = juste ? 1 : randInt(2, 9);
    if (depart - r * reg.pas === SACRIFIE_D) r = 3;                // jamais −2,7 (7 à gauche de −2)
    const x = depart - r * reg.pas;
    const ou = juste ? `la petite graduation située juste à gauche de ${frD(depart)}` : `la ${ORD(r)} petite graduation à gauche de ${frD(depart)}`;
    const texte = `<p>${INTRO(reg)}</p><p>Quel nombre est repéré par ${ou} ? Écris-le avec son signe.</p>`;
    const payload = makeSaisie(saisieD(x), texte, { ...opts(L_NOMBRE), elements_additionnels: imgElement(svgDroite(reg), 'large') });
    payload.contenu = { ...payload.contenu, sousType: juste ? 'juste_a_gauche' : 'rang', explications: { regle: EXPLICATIONS.regle, rang_depuis_la_gauche: EXPLICATIONS.rang_depuis_la_gauche } };
    payload._meta.params = { regime: reg.nom, start: reg.start, end: reg.end, depart, r, x };
    return payload;
};

// ─── Carte 3a : entre quels entiers ? (QCM) ──────────────────────────────

const entre = (bas) => `entre ${frD(bas)} et ${frD(bas + 10)}`;

const genEncadrerQcm = () => {
    for (let essai = 0; essai < 100; essai++) {
        const { reg, x } = tirerA({ nom: randomChoice(['demis', 'dixiemes']) });
        const bas = Math.floor(x / 10) * 10;
        const bon = entre(bas);
        const cands = [
            { code: 'encadrement_decale', v: bas + 10 },
            { code: 'signe_oublie', v: -bas - 10 },
            { code: 'encadrement_decale', v: bas - 10 },
        ];
        const faux = [];
        for (const c of cands) { const l = entre(c.v); if (l !== bon && !faux.some((f) => f.l === l)) faux.push({ ...c, l }); }
        if (faux.length < 3) continue;
        const choix = [bon, ...faux.map((f) => f.l)];
        const svg = svgDroite(reg, [{ d: x, nom: 'A' }]);
        const payload = makeQcm(choix, bon, `<p>${INTRO(reg)}</p><p><b>Le point A est…</b></p>`, { ...opts(L_ENCADRER), elements_additionnels: imgElement(svg, 'large') });
        payload.contenu = { ...payload.contenu, sousType: reg.nom, explications: EXPLICATIONS };
        payload._meta.params = { regime: reg.nom, start: reg.start, end: reg.end, x, bas, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error('gen_1235 : QCM encadrer sans tirage');
};

// ─── Carte 3b : Vrai ou faux ──────────────────────────────────────────────

const genVf = () => {
    for (let essai = 0; essai < 200; essai++) {
        const reg = tirerRegime(randomChoice(['demis', 'dixiemes']));
        const pts = tirerPoints(reg, 3, 2).sort((a, b) => a - b);
        if (pts.length < 3 || pts[1] - pts[0] < 3 * reg.pas || pts[2] - pts[1] < 3 * reg.pas) continue;
        const noms = ['A', 'B', 'C'];
        const P = randInt(0, 2); const Q = randomChoice([0, 1, 2].filter((i) => i !== P)); const R = [0, 1, 2].find((i) => i !== P && i !== Q);
        const x = pts[P];
        if (x >= 0) continue;
        const leurre = randomChoice(leurresAbscisse(x, reg));
        const bas = Math.floor(x / 10) * 10;
        const candidats = [
            { l: `L'abscisse de ${noms[P]} est ${frD(x)}.`, v: true, fam: 'abscisse' },
            { l: `L'abscisse de ${noms[P]} est ${frD(leurre.v)}.`, v: false, fam: 'abscisse', code: leurre.code },
            { l: `${noms[P]} est ${entre(bas)}.`, v: true, fam: 'entre' },
            { l: `${noms[P]} est ${entre(bas + 10)}.`, v: false, fam: 'entre', code: 'encadrement_decale' },
            { l: `${noms[P]} est ${entre(-bas - 10)}.`, v: false, fam: 'entre', code: 'signe_oublie' },
            { l: `L'abscisse de ${noms[R]} est ${pts[R] < 0 ? 'négative' : 'positive'}.`, v: true, fam: 'signe' },
            { l: `L'abscisse de ${noms[R]} est ${pts[R] < 0 ? 'positive' : 'négative'}.`, v: false, fam: 'signe' },
            { l: `${noms[P]} est plus proche de 0 que ${noms[Q]}.`, v: Math.abs(x) < Math.abs(pts[Q]), fam: 'proche' },
        ];
        const nbFaux = randInt(1, 3);
        const fausses = shuffle(candidats.filter((c) => !c.v)); const vraies = shuffle(candidats.filter((c) => c.v));
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (lignes.length !== 4 || new Set(lignes.map((c) => c.fam)).size !== 4) continue;
        const svg = svgDroite(reg, pts.map((d, i) => ({ d, nom: noms[i] })));
        const payload = makeVraiFaux(lignes.map((c) => c.l), lignes.map((c) => (c.v ? 0 : 1)), `<p>${INTRO(reg)}</p><p><b>Vrai ou faux ?</b></p>`, { ...opts(L_ENCADRER), elements_additionnels: imgElement(svg, 'large') });
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { regime: reg.nom, start: reg.start, end: reg.end, points: pts, x, distracteurs: lignes.filter((c) => c.code).map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error('gen_1235 : V/F sans tirage');
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'dixièmes entiers ; régimes entiers / demis / dixièmes ; le point interrogé jamais sur un entier étiqueté ; saisies négatives seulement',
        'leurres dans la fenêtre dessinée et sur la grille ; −2,7 sacrifié (partagé avec 1234)',
    ],
    abscisse_qcm_unique: 'sens_inverse_negatif (−1,3 pour −2,7, la principale), signe_inverse (2,7), rang_depuis_la_gauche (−2,3), decale_un_trait',
    encadrer_qcm_unique: 'encadrement_decale (entre −2 et −1, entre −4 et −3), signe_oublie (entre 2 et 3) ; « entre −2 et −3 » n\'est pas un distracteur',
    encadrer_tableau_vf: 'quatre familles exclusives : l\'abscisse, l\'encadrement, le signe, le plus proche de 0',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    abscisse_saisie: genAbscisseSaisie,
    abscisse_qcm_unique: genAbscisseQcm,
    nombre_saisie: genNombreSaisie,
    encadrer_qcm_unique: genEncadrerQcm,
    encadrer_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'abscisse', label: L_ABSCISSE, formatsList: [{ key: 'abscisse_saisie', label: 'Saisie' }, { key: 'abscisse_qcm_unique', label: 'QCM' }] },
    { id: 'nombre', label: L_NOMBRE, formatsList: [{ key: 'nombre_saisie', label: 'Saisie' }] },
    { id: 'encadrer', label: L_ENCADRER, formatsList: [{ key: 'encadrer_qcm_unique', label: 'QCM' }, { key: 'encadrer_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
